import { Button, Stack, useTheme } from "@mui/material"
import { observer } from "mobx-react"

import { t } from "@lingui/macro"

import { useEffect, useMemo, useState } from "react"

import { LabelQuestionTextField } from "./LabelQuestionTextField"

import { BottomContainerSection } from "./BottomContainerSection"

import { ExternalFieldTypeDropdown } from "./ExternalFieldTypeDropdown"

import { ExternalFieldRelationshipDropdown } from "./ExternalFieldRelationshipDropdown"

import { DynamicFieldKeyDropdown } from "./DynamicFieldKeyDropdown"

import { FormDetailStore } from "src/modals/contact-forms-form-builder/store"
import {
    CardContainer,
    CardInnerContainer,
    DraggableIconContainer,
    Separator,
} from "src/modals/contact-forms-form-builder/styled"

import { IExternalDynamicFieldsSectionProps } from "src/modals/contact-forms-form-builder/types/dynamicFieldSection"

import { useWindowWide } from "src/views/feature-toggles/features/useWindowWide"
import { useStore } from "src/store/lib/useStore"
import { Add24 } from "src/components/icons/Add24"
import { Icon } from "src/components/icons"
import { DragIndicator } from "src/components/icons/DragIndicator"
import { isBinButtonDisabled } from "src/modals/contact-forms-form-builder/helper/isBinButtonDisabled"
import { ISourceProps } from "src/modals/contact-forms-form-builder/types/formFieldsSection"
import { AddDynamicOptionsSection } from "src/modals/contact-forms-form-builder/DynamicViewComponents/AddDynamicOptionsSection"
import { HelpingInputText } from "src/modals/contact-forms-form-builder/DynamicViewComponents/HelpingInputText"
import { FieldTypeDropdown } from "src/components/Inputs/FieldTypeDropdown"
import { FieldTypeDropdownKeys } from "src/components/Inputs/types/IFieldTypeDropdown"
import { supportsHiding } from "src/modals/contact-forms-form-builder/helper/supportsHiding"
import { FIELD_TYPE_DYNAMIC_FIELD } from "src/modals/contact-forms-form-builder/constants/fieldTypes"
import { findOptionIndexByMetadata } from "src/modals/contact-forms-form-builder/helper/metadataUtils"

const getFormTypeDropdownValue = (type: string): FieldTypeDropdownKeys => {
    switch (type) {
        case "select":
            return FieldTypeDropdownKeys.Dropdown
        case "radio":
            return FieldTypeDropdownKeys.RadioButton
        case "textarea":
            return FieldTypeDropdownKeys.Paragraph
        case "checkbox":
            return FieldTypeDropdownKeys.Checkbox
        case "text":
            return FieldTypeDropdownKeys.ShortAnswer
        case "file":
            return FieldTypeDropdownKeys.FileUpload
        default:
            return FieldTypeDropdownKeys.ShortAnswer
    }
}

export const ExternalDynamicFieldsSection = observer(
    (props: IExternalDynamicFieldsSectionProps) => {
        const store = useStore(FormDetailStore)
        const theme = useTheme()
        const [relationshipArray, setRelationshipArray] = useState<
            ISourceProps[]
        >([])
        const [isHelpTextVisible, setIsHelpTextVisible] = useState(false)
        const currentWidth = useWindowWide(0)
        const externalFieldsArray = store.formFields.get("externalFieldList")
        const noRelation = t`contact-form-forms-detail-modal.form-fields-settings-external-field-relationship-dropdown.default-value`

        useEffect(() => {
            const list = [...props.externalDynamicFieldsList]
            const array = list.map((item) => ({
                key: item.question.value,
                value: item.uuid,
            }))
            setRelationshipArray([
                { key: noRelation, value: noRelation },
                ...array,
            ])
            if (
                props.externalDynamicFieldsList[props.index].helpingText.value
                    .length > 0
            ) {
                setIsHelpTextVisible(true)
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [props.externalDynamicFieldsList])

        const onSelectExternalFieldTypeValueChange = (value: string) => {
            let data = [...props.externalDynamicFieldsList]
            const obj = JSON.parse(JSON.stringify(data[props.index]))
            obj.externalFieldTypeDropdownValue.errorMessage = ""
            store.setHasError(false)
            obj.externalFieldTypeDropdownValue.value =
                value.trim() !== "" ? value : ""

            const selectedSource = externalFieldsArray?.find(
                (item) => item.source === value,
            )
            obj.sourceTypes = selectedSource?.types
            obj.isAddOptionsVisible = selectedSource?.internal ?? false

            // Reset dynamic field metadata when source changes
            obj.dynamicFieldMetadata = {}

            // Handle dynamic field sources differently from regular sources
            if (
                selectedSource?.types?.includes(FIELD_TYPE_DYNAMIC_FIELD) ===
                true
            ) {
                const connectorId = store.formFields.get("selectedConnectorId")
                if (connectorId !== undefined && connectorId > 0) {
                    void store.getDynamicFieldsForSource(connectorId, value)
                }
                // Field type will be set when user selects a dynamic field option
                obj.fieldTypeDropdownValue.value = ""
            } else {
                // For non-dynamic sources, derive field type from source type (use first type)
                obj.fieldTypeDropdownValue.value = getFormTypeDropdownValue(
                    selectedSource?.types?.[0] ?? "",
                )
            }
            // Note: We don't clear dynamic field options when switching away from a dynamic source
            // because other fields may still be using their cached options

            if (value.trim() === "") {
                store.setHasError(true)
                obj.externalFieldTypeDropdownValue.errorMessage = t`errors.required`
            }
            data[props.index] = obj
            props.setExternalDynamicFieldsList(data)
        }

        const onSelectDynamicFieldChange = (selectedIndex: number) => {
            let data = [...props.externalDynamicFieldsList]
            const obj = JSON.parse(JSON.stringify(data[props.index]))

            obj.fieldTypeDropdownValue.errorMessage = ""
            store.setHasError(false)

            // Get options for the current source and find the selected field by index
            const source =
                props.externalDynamicFieldsList[props.index]
                    .externalFieldTypeDropdownValue.value
            const options = store.getDynamicFieldOptionsForSource(source)
            const selectedDynamicField = options[selectedIndex]

            if (selectedDynamicField !== undefined) {
                obj.fieldTypeDropdownValue.value = getFormTypeDropdownValue(
                    selectedDynamicField.field_type,
                )
                obj.dynamicFieldMetadata = { ...selectedDynamicField.metadata }
                // Always set required, defaulting to false if undefined
                obj.isRequired = selectedDynamicField.required ?? false
            } else {
                // Reset to consistent "no selection" state
                obj.fieldTypeDropdownValue.value = ""
                obj.dynamicFieldMetadata = {}
                obj.isRequired = false
            }
            data[props.index] = obj
            props.setExternalDynamicFieldsList(data)
        }

        const isDynamicFieldSource =
            props.externalDynamicFieldsList[props.index].sourceTypes?.includes(
                FIELD_TYPE_DYNAMIC_FIELD,
            ) ?? false

        const onSelectRelationshipValueChange = (value: string) => {
            let data = [...props.externalDynamicFieldsList]
            const obj = JSON.parse(JSON.stringify(data[props.index]))
            obj.relationshipDropdownValue.errorMessage = ""
            store.setHasError(false)
            obj.relationshipDropdownValue.value =
                value.trim() !== "" ? value : ""
            if (value.trim() === "") {
                store.setHasError(true)
                obj.question.errorMessage = t`errors.required`
            }
            data[props.index] = obj
            props.setExternalDynamicFieldsList(data)
        }

        const handleQuestionFormChange = (
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => {
            const list = [...props.externalDynamicFieldsList]
            const obj = JSON.parse(JSON.stringify(list[props.index]))
            const value = event.target.value
            obj.question.errorMessage = ""
            store.setHasError(false)
            obj.question.value = value.trim() !== "" ? value : ""
            if (value.trim() === "") {
                store.setHasError(true)
                obj.question.errorMessage = t`errors.required`
            }
            list[props.index] = obj
            props.setExternalDynamicFieldsList(list)
        }

        const handleHelpingTextChange = (
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => {
            const list = [...props.externalDynamicFieldsList]
            const obj = JSON.parse(JSON.stringify(list[props.index]))
            const value = event.target.value
            obj.helpingText.errorMessage = ""
            obj.helpingText.value = value.trim() !== "" ? value : ""
            if (value.trim() === "") {
                obj.helpingText.errorMessage = t`errors.required`
            }
            list[props.index] = obj
            props.setExternalDynamicFieldsList(list)
        }

        const onToggleHelpText = () => {
            setIsHelpTextVisible(!isHelpTextVisible)
            handleHelpingTextChange({
                target: { value: "" },
            } as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>)
            store.setHasError(false)
        }

        const onSelectValueChange = (value: string) => {
            let data = [...props.externalDynamicFieldsList]
            const obj = JSON.parse(JSON.stringify(data[props.index]))
            obj.fieldTypeDropdownValue.errorMessage = ""
            store.setHasError(false)
            obj.fieldTypeDropdownValue.value = value.trim() !== "" ? value : ""
            if (value.trim() === "") {
                store.setHasError(true)
                obj.fieldTypeDropdownValue.errorMessage = t`errors.required`
            }
            data[props.index] = obj
            props.setExternalDynamicFieldsList(data)
        }
        const handleInputChange = (
            index: number,
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => {
            const list = [...props.externalDynamicFieldsList]
            const obj = JSON.parse(JSON.stringify(list[props.index]))
            const value = event.target.value
            obj.inputList[index].errorMessage = ""
            store.setHasError(false)
            obj.inputList[index].value = value.trim() !== "" ? value : ""
            if (value.trim() === "") {
                store.setHasError(true)
                obj.inputList[index].errorMessage = t`errors.required`
            }
            list[props.index] = obj
            props.setExternalDynamicFieldsList(list)
        }
        const getSourcesList = () => {
            return relationshipArray.filter(
                (item) =>
                    item.value !==
                    props.externalDynamicFieldsList[props.index].uuid,
            )
        }
        const handleRemoveClick = (index: number) => {
            store.setHasError(false)
            const list = [...props.externalDynamicFieldsList]
            list[props.index].inputList.splice(index, 1)
            props.setExternalDynamicFieldsList(list)
        }

        const handleAddClick = () => {
            const list = [...props.externalDynamicFieldsList]
            list[props.index].inputList = [
                ...list[props.index].inputList,
                { value: "", errorMessage: "" },
            ]
            props.setExternalDynamicFieldsList(list)
        }

        const shouldShowRelationshipDropdown = () =>
            props.externalDynamicFieldsList[props.index].fieldTypeDropdownValue
                .value === FieldTypeDropdownKeys.Dropdown ||
            props.externalDynamicFieldsList[props.index].fieldTypeDropdownValue
                .value === FieldTypeDropdownKeys.Checkbox ||
            props.externalDynamicFieldsList[props.index].fieldTypeDropdownValue
                .value === FieldTypeDropdownKeys.RadioButton

        const fieldTypeDropdownOptions = useMemo(
            () =>
                props.externalDynamicFieldsList[props.index].isAddOptionsVisible
                    ? [
                          FieldTypeDropdownKeys.Dropdown,
                          FieldTypeDropdownKeys.ShortAnswer,
                      ]
                    : [],
            [props.externalDynamicFieldsList, props.index],
        )

        // Compute options once per render - MobX observer handles reactivity
        const dynamicFieldOptions = store.getDynamicFieldOptionsForSource(
            props.externalDynamicFieldsList[props.index]
                .externalFieldTypeDropdownValue.value,
        )

        // Compute selected index for dynamic field dropdown
        const dynamicFieldSelectedIndex = (() => {
            const metadata =
                props.externalDynamicFieldsList[props.index]
                    .dynamicFieldMetadata
            if (
                metadata === undefined ||
                metadata === null ||
                Object.keys(metadata).length === 0
            ) {
                return -1
            }
            return findOptionIndexByMetadata(
                dynamicFieldOptions,
                metadata as Record<string, unknown>,
            )
        })()

        return (
            <CardContainer windowWidth={currentWidth}>
                <CardInnerContainer>
                    <DraggableIconContainer>
                        <DragIndicator />
                    </DraggableIconContainer>
                    <Stack spacing={2}>
                        <ExternalFieldTypeDropdown
                            sources={externalFieldsArray ?? []}
                            selectedValue={
                                props.externalDynamicFieldsList[props.index]
                                    .externalFieldTypeDropdownValue.value
                            }
                            onSelectionChange={
                                onSelectExternalFieldTypeValueChange
                            }
                            errorMessage={
                                props.externalDynamicFieldsList[props.index]
                                    .externalFieldTypeDropdownValue.errorMessage
                            }
                        />
                        {isDynamicFieldSource && (
                            <DynamicFieldKeyDropdown
                                options={dynamicFieldOptions}
                                selectedIndex={dynamicFieldSelectedIndex}
                                onSelectionChange={onSelectDynamicFieldChange}
                                errorMessage={
                                    props.externalDynamicFieldsList[props.index]
                                        .fieldTypeDropdownValue.errorMessage
                                }
                                isLoading={store.isLoadingDynamicFieldSource(
                                    props.externalDynamicFieldsList[props.index]
                                        .externalFieldTypeDropdownValue.value,
                                )}
                            />
                        )}
                        {props.externalDynamicFieldsList[props.index]
                            .externalFieldTypeDropdownValue.value.length > 0 ? (
                            <>
                                <FieldTypeDropdown
                                    key={props.index}
                                    item={
                                        props.externalDynamicFieldsList[
                                            props.index
                                        ].fieldTypeDropdownValue
                                    }
                                    onSelectValueChange={onSelectValueChange}
                                    disabled={
                                        !props.externalDynamicFieldsList[
                                            props.index
                                        ].isAddOptionsVisible ||
                                        isDynamicFieldSource
                                    }
                                    dropdownOptions={fieldTypeDropdownOptions}
                                />
                                {shouldShowRelationshipDropdown() ? (
                                    <ExternalFieldRelationshipDropdown
                                        sources={getSourcesList()}
                                        selectedValue={
                                            props.externalDynamicFieldsList[
                                                props.index
                                            ].relationshipDropdownValue.value
                                        }
                                        onSelectionChange={
                                            onSelectRelationshipValueChange
                                        }
                                        errorMessage={
                                            props.externalDynamicFieldsList[
                                                props.index
                                            ].relationshipDropdownValue
                                                .errorMessage
                                        }
                                    />
                                ) : null}
                            </>
                        ) : null}

                        <LabelQuestionTextField
                            index={props.index}
                            item={
                                props.externalDynamicFieldsList[props.index]
                                    .question
                            }
                            handleFormChange={handleQuestionFormChange}
                        />
                        {props.externalDynamicFieldsList[props.index]
                            .fieldTypeDropdownValue.value ===
                            FieldTypeDropdownKeys.Dropdown &&
                        props.externalDynamicFieldsList[props.index]
                            .isAddOptionsVisible ? (
                            <>
                                {props.inputList.map((input, index) => {
                                    return (
                                        <AddDynamicOptionsSection
                                            type={
                                                FieldTypeDropdownKeys.Dropdown
                                            }
                                            index={index}
                                            item={input}
                                            handleInputChange={
                                                handleInputChange
                                            }
                                            handleRemoveClick={
                                                handleRemoveClick
                                            }
                                            disabled={store.isEditMode}
                                        />
                                    )
                                })}
                                <Button
                                    sx={{ alignSelf: "flex-start" }}
                                    startIcon={
                                        <Icon
                                            icon={<Add24 />}
                                            size={20}
                                            color={
                                                store.isEditMode
                                                    ? theme.palette.info.light
                                                    : theme.palette.info.main
                                            }
                                        />
                                    }
                                    disabled={store.isEditMode}
                                    color="info"
                                    onClick={handleAddClick}
                                >
                                    {t`contact-form-forms-detail-modal.form-field-section.add-options-label`}
                                </Button>
                            </>
                        ) : null}

                        {isHelpTextVisible && (
                            <HelpingInputText
                                helpingText={
                                    props.externalDynamicFieldsList[props.index]
                                        .helpingText
                                }
                                handleHelpingTextChange={
                                    handleHelpingTextChange
                                }
                            />
                        )}

                        <Separator />
                        <BottomContainerSection
                            variant="default"
                            index={props.index}
                            isRequired={props.isRequired ?? false}
                            isHidden={props.isHidden ?? false}
                            fallbackOption={props.fallbackOption}
                            forceUser={props.forceUser ?? false}
                            fieldType={
                                props.externalDynamicFieldsList[props.index]
                                    .fieldTypeDropdownValue.value
                            }
                            handleSwitchChange={() =>
                                props.handleSwitchChange(props.index)
                            }
                            handleHiddenChange={
                                props.handleHiddenChange !== undefined &&
                                supportsHiding(
                                    props.externalDynamicFieldsList[props.index]
                                        .fieldTypeDropdownValue.value,
                                )
                                    ? () =>
                                          props.handleHiddenChange?.(
                                              props.index,
                                          )
                                    : undefined
                            }
                            handleFallbackOptionChange={
                                props.handleFallbackOptionChange !== undefined
                                    ? (index: number, value: string) =>
                                          props.handleFallbackOptionChange?.(
                                              index,
                                              value,
                                          )
                                    : undefined
                            }
                            handleForceUserChange={
                                props.handleForceUserChange !== undefined
                                    ? () =>
                                          props.handleForceUserChange?.(
                                              props.index,
                                          )
                                    : undefined
                            }
                            handleContentCopyClick={() =>
                                props.handleContentCopyClick(props.index)
                            }
                            handleDeleteClick={() =>
                                props.handleRemoveClick(props.index)
                            }
                            disableButton={isBinButtonDisabled(
                                props.externalDynamicFieldsList,
                            )}
                            handleHelpTextClick={onToggleHelpText}
                        />
                    </Stack>
                </CardInnerContainer>
            </CardContainer>
        )
    },
)
